import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Video, Phone, X } from "lucide-react";
import { toast } from "sonner";
import { api, useAuth } from "@/context/AuthContext";

/* Global practice-invite notifier.

   Mounted once for signed-in users (App.js). Polls /friends/invites every
   few seconds and pops a corner card — "You've been invited to a mock call
   by [Name]" — the moment a new incoming invite appears, no refresh needed.

   - Dedupe: each invite id pops at most once per session (seenRef).
   - Suppressed on /video-call so it never overlays a live call; those
     invites are NOT marked seen, so a still-valid invite pops after the
     call ends (expired ones are swept server-side at read time).
   - Accept routes through /pre-call (device check) before /video-call.
   - Offline recipients are emailed by the backend instead — this listener
     is the online-only path. */

const POLL_MS = 5000;

const initialsOf = (name = "?") => (name || "?").charAt(0).toUpperCase();

export const InviteNotificationListener = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]); // incoming invite payloads on screen
  const seenRef = useRef(new Set());

  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    let inFlight = false;

    const tick = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const res = await api.get("/friends/invites");
        if (!alive) return;
        // Suppress while in a live call — leave invites unseen so they can
        // still surface afterwards if they haven't expired. Also suppress on
        // /friends: the page renders its own inline invite UI, and a second
        // owner polling + acting on the same invite can race the navigation.
        const path = window.location.pathname;
        if (path.startsWith("/video-call") || path.startsWith("/friends")) return;
        const incoming = res.data?.incoming || [];
        setCards((prev) => {
          const incomingIds = new Set(incoming.map((i) => i.invite_id));
          // Drop cards whose invite vanished (accepted/declined elsewhere,
          // or expired) so the toast never goes stale.
          const kept = prev.filter((c) => incomingIds.has(c.invite_id));
          const keptIds = new Set(kept.map((c) => c.invite_id));
          const fresh = incoming.filter(
            (i) => !seenRef.current.has(i.invite_id) && !keptIds.has(i.invite_id)
          );
          fresh.forEach((i) => seenRef.current.add(i.invite_id));
          return [...kept, ...fresh];
        });
      } catch {
        /* network hiccup — try again next tick */
      } finally {
        inFlight = false;
      }
    };

    tick();
    const iv = setInterval(tick, POLL_MS);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, [user]);

  const removeCard = (id) =>
    setCards((prev) => prev.filter((c) => c.invite_id !== id));

  const accept = async (inv) => {
    try {
      const res = await api.post(`/friends/invites/${inv.invite_id}/accept`);
      removeCard(inv.invite_id);
      navigate("/pre-call", {
        state: {
          mode: "friend",
          room: res.data.room_name,
          cameraOn: res.data.call_mode !== "phone",
        },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not accept invite.");
      removeCard(inv.invite_id);
    }
  };

  const decline = async (inv) => {
    try {
      await api.post(`/friends/invites/${inv.invite_id}/decline`);
    } catch {
      toast.error("Could not decline invite.");
    }
    removeCard(inv.invite_id);
  };

  if (!user) return null;

  return (
    <div className="pointer-events-none fixed bottom-5 right-5 z-[90] flex w-[340px] max-w-[calc(100vw-2.5rem)] flex-col gap-3">
      <AnimatePresence>
        {cards.map((inv) => {
          const name =
            inv.from_user?.name || inv.from_user?.username || "A friend";
          return (
            <motion.div
              key={inv.invite_id}
              data-testid={`invite-toast-${inv.invite_id}`}
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.96 }}
              transition={{ type: "spring", stiffness: 320, damping: 26 }}
              className="pointer-events-auto relative overflow-hidden rounded-2xl border border-[#E52525]/40 bg-[#141414] p-4 shadow-[0_12px_40px_rgba(0,0,0,0.6)]"
            >
              <span
                aria-hidden="true"
                className="absolute inset-x-0 top-0 h-0.5 bg-[#E52525]"
              />
              <button
                type="button"
                aria-label="Dismiss"
                data-testid={`invite-toast-dismiss-${inv.invite_id}`}
                onClick={() => removeCard(inv.invite_id)}
                className="absolute right-2.5 top-2.5 flex h-6 w-6 items-center justify-center rounded-full text-zinc-500 transition-colors hover:bg-white/5 hover:text-white"
              >
                <X size={13} />
              </button>

              <div className="flex items-center gap-3">
                {inv.from_user?.picture ? (
                  <img
                    src={inv.from_user.picture}
                    alt={name}
                    className="h-10 w-10 shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E52525]/15 text-sm font-bold text-[#FF5A5A]">
                    {initialsOf(name)}
                  </span>
                )}
                <div className="min-w-0 flex-1 pr-5">
                  <p className="text-sm font-semibold leading-snug text-white">
                    You've been invited to a{" "}
                    {inv.call_mode === "phone" ? (
                      <>
                        <span className="text-[#FF5A5A]">phone mock call</span>
                        <span className="font-normal text-zinc-400"> (camera off)</span>
                      </>
                    ) : (
                      <span className="text-[#FF5A5A]">video mock call</span>
                    )}{" "}
                    by <span className="text-[#FF5A5A]">{name}</span>
                  </p>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    Expires in 15 minutes
                  </p>
                </div>
              </div>

              <div className="mt-3.5 flex items-center gap-2">
                <button
                  type="button"
                  data-testid={`invite-toast-decline-${inv.invite_id}`}
                  onClick={() => decline(inv)}
                  className="flex-1 rounded-full border border-white/10 px-3 py-2 text-xs font-semibold text-zinc-300 transition-colors hover:border-white/25 hover:text-white"
                >
                  Decline
                </button>
                <button
                  type="button"
                  data-testid={`invite-toast-accept-${inv.invite_id}`}
                  onClick={() => accept(inv)}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-[#E52525] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#c81f1f]"
                >
                  {inv.call_mode === "phone" ? (
                    <Phone size={13} />
                  ) : (
                    <Video size={13} />
                  )}{" "}
                  Accept
                </button>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};
